import { Link } from "react-router-dom";
import {
  BookOpen,
  ExternalLink,
  ArrowRight,
  Compass,
  Users,
  Target,
  UtensilsCrossed,
  Store,
  Building2,
  Megaphone,
  ShoppingBag,
  UserCheck,
  Smile,
  Eye,
} from "lucide-react";

const LOGO_BUSSOLE = "/local-assets/p0rt9rd9_logo_LeBussole-600x138.png";
const PHOTO_BREAK  = "https://images.pexels.com/photos/6699285/pexels-photo-6699285.jpeg?auto=compress&cs=tinysrgb&w=1920&q=80";
const PHOTO_CTA    = "/local-assets/v3tffhm7_Tessera-2026.png";

const COSA_SONO = [
  {
    icon: BookOpen,
    titolo: "Cosa sono",
    testo: "Manuali pratici e operativi su gestione, marketing, vendite e organizzazione. Linguaggio concreto, taglio editoriale, focus sul core business.",
  },
  {
    icon: Compass,
    titolo: "Come nascono",
    testo: "Curate dal Settore Politiche per lo Sviluppo di Confcommercio insieme alle Associazioni di categoria (FIPE, FIDA, e tutte le altre del Sistema).",
  },
  {
    icon: Target,
    titolo: "A chi servono",
    testo: "A bar, ristoranti, negozi, alberghi, attività di servizi: a chiunque voglia gestire la propria impresa con strumenti manageriali, anche piccola.",
  },
];

const TEMI = [
  { icon: UtensilsCrossed, label: "Ristorazione & Bar",        color: "#f97316" }, // orange-500 — Ristorazione e Turismo
  { icon: Store,           label: "Dettaglio & Retail",        color: "#4f46e5" }, // indigo-600 — Commercio
  { icon: Building2,       label: "Hospitality",               color: "#0d9488" }, // teal-600 — Immobiliare/Turismo
  { icon: Megaphone,       label: "Marketing & Comunicazione", color: "#f59e0b" }, // amber-500 — Professioni
  { icon: ShoppingBag,     label: "Vendite",                   color: "#16a34a" }, // green-600 — Alimentare
  { icon: UserCheck,       label: "Gestione del personale",    color: "#475569" }, // slate-600 — Mobilità
  { icon: Smile,           label: "Customer Experience",       color: "#e11d48" }, // rose-600 — Sanità
  { icon: Eye,             label: "Visual Merchandising",      color: "#0284c7" }, // sky-600 — Servizi e ICT
];

const COME_FUNZIONA = [
  {
    num: "01",
    titolo: "Scegli la tua Bussola",
    testo: "Sfoglia la collana e individua il manuale o l'approfondimento più utile per la tua attività.",
  },
  {
    num: "02",
    titolo: "Accedi con la tessera",
    testo: "Registrati su associati.confcommercio.it con il numero della tua tessera Confcommercio Roma.",
  },
  {
    num: "03",
    titolo: "Scarica in ePub",
    testo: "Scarica gratuitamente fino a 3 Bussole in formato ePub, da leggere ovunque, anche offline.",
  },
];

export default function BussolePage() {
  return (
    <main id="main-content">

      {/* ── HERO ── */}
      <section className="relative h-[280px] md:h-[340px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1920&q=60&auto=format"
          alt="Le Bussole"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">La collana di Confcommercio</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">Le Bussole</h1>
          <p className="text-white/80 text-base md:text-lg mt-3 max-w-2xl leading-relaxed">
            Manuali pratici, idee e strumenti operativi per chi fa impresa nel commercio, nel turismo e nei servizi.
          </p>
        </div>
      </section>

      {/* ── INTRO + 3 CARD ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Logo + paragrafo introduttivo */}
          <div className="grid md:grid-cols-2 gap-10 items-center mb-14">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Idee per chi fa impresa</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-5 font-['Encode_Sans_Semi_Condensed'] leading-tight">
                Competenze manageriali alla portata di tutte le imprese
              </h2>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                Un bar, un ristorante, un negozio, un albergo o un'attività di servizi è prima di tutto un'impresa. Per crescere servono <strong className="text-[var(--confcom-text-primary)]">marketing, organizzazione, pianificazione e capacità di adattarsi al cambiamento</strong>: <em>Le Bussole</em> sono il modo più diretto per acquisirle.
              </p>
            </div>
            <div className="bg-white border border-[var(--confcom-border)] rounded-2xl px-8 py-10 flex items-center justify-center shadow-sm">
              <img
                src={LOGO_BUSSOLE}
                alt="Le Bussole — Idee per chi fa impresa | Confcommercio"
                className="max-h-24 w-full object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </div>

          {/* 3 card narrative */}
          <div className="grid md:grid-cols-3 gap-6">
            {COSA_SONO.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.titolo}
                  className="rounded-2xl border border-[var(--confcom-border)] bg-[#F8FAFD] p-7 hover:shadow-md transition-shadow"
                  data-testid={`bussole-intro-${c.titolo.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[var(--confcom-blue)] flex items-center justify-center mb-5">
                    <Icon size={22} className="text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                    {c.titolo}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                    {c.testo}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TEMI DEI MANUALI ── */}
      <section className="py-16 bg-[#F8FAFD]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">La collana</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed']">
              I temi dei manuali
            </h2>
            <p className="text-[var(--confcom-text-secondary)] mt-3 max-w-2xl mx-auto">
              Dalla gestione della sala al visual merchandising, dal personale al marketing digitale: ogni Bussola è dedicata a un'area strategica per la tua impresa.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TEMI.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.label}
                  className="group bg-white border border-[var(--confcom-border)] rounded-xl p-5 flex flex-col items-start gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`bussole-tema-${t.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: t.color + "1A" }}
                  >
                    <Icon size={20} style={{ color: t.color }} aria-hidden="true" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug">
                    {t.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://lebussole.confcommercio.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity text-sm"
            >
              <BookOpen size={16} aria-hidden="true" /> Esplora la collana completa <ExternalLink size={14} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* ── FOTO DI ROTTURA ── */}
      <div className="relative h-72 md:h-80 overflow-hidden">
        <img
          src={PHOTO_BREAK}
          alt="Professionista che legge"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(4,30,66,0.88) 0%, rgba(4,30,66,0.55) 60%, rgba(4,30,66,0.20) 100%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex items-center">
          <div className="max-w-xl">
            <span className="block text-[#56B7E6] text-xs font-bold uppercase tracking-[0.2em] mb-3">La nostra promessa</span>
            <p className="text-white text-2xl md:text-3xl font-bold font-['Encode_Sans_Semi_Condensed'] leading-snug">
              Strumenti pratici<br />per chi fa impresa ogni giorno.
            </p>
          </div>
        </div>
      </div>

      {/* ── COME FUNZIONA ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Come accedere</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed']">
              Tre passi per scaricare le tue Bussole
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {COME_FUNZIONA.map((s) => (
              <div
                key={s.num}
                className="rounded-2xl border border-[var(--confcom-border)] p-7 bg-white"
                data-testid={`bussole-step-${s.num}`}
              >
                <span
                  className="block font-['Encode_Sans_Semi_Condensed'] text-5xl font-extrabold mb-3"
                  style={{ color: "#E2E8F0" }}
                >
                  {s.num}
                </span>
                <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                  {s.titolo}
                </h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                  {s.testo}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SCARICA ── */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl overflow-hidden shadow-xl bg-[var(--confcom-blue)] grid md:grid-cols-2 min-h-[320px]">
            {/* Colonna sinistra — tessera */}
            <div className="relative hidden md:flex items-center justify-center bg-[#0a2a5e] min-h-[320px] p-8">
              <img
                src={PHOTO_CTA}
                alt="Tessera associato Confcommercio 2026"
                className="w-full max-w-sm object-contain drop-shadow-2xl"
              />
            </div>

            {/* Colonna destra — contenuto */}
            <div className="px-10 py-12 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-[#56B7E6]/20 border border-[#56B7E6]/40 text-[#56B7E6] text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full w-fit mb-6">
                <BookOpen size={13} /> Benefit riservato agli associati
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] leading-tight mb-4">
                Sei un associato?<br />Scarica fino a 3 Bussole gratis
              </h2>
              <p className="text-white/80 leading-relaxed mb-8">
                Gli associati Confcommercio Roma possono scaricare <strong className="text-white">gratuitamente fino a 3 Bussole</strong> in formato ePub. Accedi con il numero della tua tessera su <span className="text-[#56B7E6]">associati.confcommercio.it</span>.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://associati.confcommercio.it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity shadow-lg text-sm"
                  data-testid="bussole-cta-scarica"
                >
                  Registrati e scarica <ExternalLink size={15} aria-hidden="true" />
                </a>
                <a
                  href="https://lebussole.confcommercio.it/approfondimenti/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border-2 border-white/40 text-white/90 px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity text-sm"
                >
                  Leggi gli approfondimenti <ExternalLink size={15} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── NON SEI ANCORA ASSOCIATO ── */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-[var(--confcom-border)] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 bg-[#F8FAFD]">
            <div className="w-14 h-14 rounded-xl bg-[var(--confcom-blue)] flex items-center justify-center shrink-0">
              <Users size={26} className="text-white" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Non sei ancora associato?</span>
              <h3 className="text-xl md:text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mt-2 mb-2">
                Scopri tutti i vantaggi di Confcommercio Roma
              </h3>
              <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">
                Accedi alle Bussole, alle convenzioni, ai corsi di formazione e ai servizi dedicati alle imprese del terziario.
              </p>
            </div>
            <Link
              to="/associati-ora"
              className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-6 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm shrink-0"
              data-testid="bussole-cta-associati"
            >
              Associati ora <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
